import React, { useState, useEffect } from 'react';
import Card from '~/src/components/Card';
import ModalOverlay from '~/src/components/Modal/Overlay';
import MiniSpinner from '~/src/components/Spinner/MiniSpinner';
import InlineNotification from '~/src/components/Notification/InlineNotification';
import cbsSubscriptionsService from '~/src/services/api/cbsSubscriptionsService';
import useUser from '~/src/hooks/useUser';
import { BillingCyclePreviewModal } from './BillingCyclePreviewModal';
import {
  SELF_SERVE_BILLING_CYCLES,
  NON_SELF_SERVE_BILLING_CYCLES,
  getAllBillingCycleOptions,
} from './constants';
import { useCbsSubscriptionDetails } from '~/src/hooks/cbs/useCbsSubscriptionDetails';

interface BillingCycleCardProps {
  subscription: any;
}

export const BillingCycleCard = ({ subscription }: BillingCycleCardProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<
    string | null
  >(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBillingCycle, setCurrentBillingCycle] = useState<string | null>(
    null,
  );
  const { currentOrganization } = useUser();

  const {
    data: cbsDetails,
    isLoading: detailsLoading,
    error: cbsDetailsError,
    refetch: refetchCbsDetails,
  } = useCbsSubscriptionDetails();

  const hasPendingChange =
    cbsDetails?.has_pending_billing_cycle_change || false;

  const cbsError = cbsDetailsError
    ? 'Unable to connect to billing service. Please try again later.'
    : null;

  useEffect(() => {
    setCurrentBillingCycle(
      subscription?.plan?.interval || SELF_SERVE_BILLING_CYCLES.MONTHLY,
    );
  }, [subscription?.plan]);

  const handleBillingCycleChange = async (newBillingCycle: string) => {
    if (newBillingCycle === currentBillingCycle) return;

    setLoading(true);
    setError(null);
    setPreviewData(null);
    setSelectedBillingCycle(newBillingCycle);

    try {
      const orgFprint = currentOrganization?.fprint;
      if (!orgFprint) {
        throw new Error('Organization fingerprint not found');
      }

      const params = {
        billing_cycle: newBillingCycle,
      };

      const result = await cbsSubscriptionsService.getSubscriptionPreview(
        orgFprint,
        params,
      );

      setPreviewData(result);
      setShowModal(true);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmChange = async () => {
    if (!selectedBillingCycle || !currentOrganization?.fprint) {
      setError('Missing required information for billing cycle update');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData = {
        billing_cycle: selectedBillingCycle,
      };

      await cbsSubscriptionsService.updateSubscription(
        currentOrganization.fprint,
        updateData,
      );

      setCurrentBillingCycle(selectedBillingCycle);
      setSuccessMessage(
        `Successfully updated billing cycle to ${selectedBillingCycle}!`,
      );

      setShowModal(false);
      setPreviewData(null);
      setSelectedBillingCycle(null);

      await refetchCbsDetails();
    } catch (err: any) {
      setShowModal(false);
      setPreviewData(null);
      setSelectedBillingCycle(null);

      setError(err.message || 'Failed to update billing cycle');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelPreview = () => {
    setShowModal(false);
    setPreviewData(null);
    setSelectedBillingCycle(null);
    setError(null);
  };

  // On failure the selector is hidden by design — user restarts rather than re-submitting.
  const hasError = Boolean(error);

  // Check if current billing cycle is not available through self-serve
  const isNonSelfServeBillingCycle =
    currentBillingCycle === NON_SELF_SERVE_BILLING_CYCLES.QUARTERLY ||
    currentBillingCycle === NON_SELF_SERVE_BILLING_CYCLES.SEMI_ANNUAL;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className="text-lg font-bold">Change Billing Cycle</div>
        {!isNonSelfServeBillingCycle &&
          !hasPendingChange &&
          !cbsError &&
          !hasError && (
            <div className="flex items-center gap-2">
              {detailsLoading || loading ? (
                <MiniSpinner className="text-gray-400" />
              ) : (
                <select
                  value={currentBillingCycle || ''}
                  onChange={(e) => handleBillingCycleChange(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm bg-white min-w-[100px] h-8"
                >
                  {getAllBillingCycleOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
      </div>

      {(isNonSelfServeBillingCycle ||
        hasPendingChange ||
        cbsError ||
        error ||
        successMessage) && (
        <div className="mt-2">
          {hasPendingChange ? (
            <InlineNotification
              type="warning"
              title="Pending Billing Cycle Change"
              message="Your billing cycle is scheduled to change from 365 day to 30 day. For assistance, please contact Clio Support."
            />
          ) : isNonSelfServeBillingCycle ? (
            <InlineNotification
              type="warning"
              title="Billing Cycle Changes Unavailable"
              message={`Billing cycle changes are not available for ${currentBillingCycle} billing cycles. Please contact Clio support for assistance.`}
            />
          ) : cbsError ? (
            <InlineNotification
              type="error"
              title="Service Unavailable"
              message="Unable to connect to billing service. Please try again later."
            />
          ) : error ? (
            <InlineNotification
              type="error"
              title="Update Failed"
              message={error}
            />
          ) : successMessage ? (
            <InlineNotification
              type="success"
              title="Success"
              message={successMessage}
            />
          ) : null}
        </div>
      )}

      <ModalOverlay show={showModal} onCancel={handleCancelPreview}>
        {previewData && selectedBillingCycle && (
          <BillingCyclePreviewModal
            previewData={previewData.preview}
            onCancel={handleCancelPreview}
            onConfirm={handleConfirmChange}
            isProcessing={isProcessing}
          />
        )}
      </ModalOverlay>
    </Card>
  );
};
